import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { organisationService } from "../services/organisationService";

// Query keys for consistent cache management
export const organisationKeys = {
  all: ["organisations"],
  lists: () => [...organisationKeys.all, "list"],
  list: (filters) => [...organisationKeys.lists(), { filters }],
  details: () => [...organisationKeys.all, "detail"],
  detail: (id) => [...organisationKeys.details(), id],
  members: (id) => [...organisationKeys.all, "members", id],
  contacts: (id) => [...organisationKeys.all, "contacts", id],
  geofences: (id) => [...organisationKeys.all, "geofences", id],
  actions: (id) => [...organisationKeys.all, "actions", id],
  roles: (id) => [...organisationKeys.all, "roles", id],
};

// Custom hooks for organisations
export const useOrganisations = (options = {}) => {
  return useQuery({
    queryKey: organisationKeys.lists(),
    queryFn: organisationService.getOrganisations,
    ...options,
  });
};

export const useOrganisation = (id, options = {}) => {
  return useQuery({
    queryKey: organisationKeys.detail(id),
    queryFn: () => organisationService.getOrganisation(id),
    enabled: !!id,
    ...options,
  });
};

export const useOrganisationMembers = (id, options = {}) => {
  return useQuery({
    queryKey: organisationKeys.members(id),
    queryFn: () => organisationService.getOrganisationMembers(id),
    enabled: !!id,
    ...options,
  });
};

export const useOrganisationContacts = (id, options = {}) => {
  return useQuery({
    queryKey: organisationKeys.contacts(id),
    queryFn: () => organisationService.getOrganisationContacts(id),
    enabled: !!id,
    ...options,
  });
};

export const useOrganisationGeofences = (id, options = {}) => {
  return useQuery({
    queryKey: organisationKeys.geofences(id),
    queryFn: () => organisationService.getOrganisationGeofences(id),
    enabled: !!id,
    ...options,
  });
};

export const useOrganisationActions = (id, options = {}) => {
  return useQuery({
    queryKey: organisationKeys.actions(id),
    queryFn: () => organisationService.getOrganisationActions(id),
    enabled: !!id,
    ...options,
  });
};

// Mutation hooks for organisations
export const useCreateOrganisation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: organisationService.createOrganisation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organisationKeys.lists() });
    },
  });
};

export const useUpdateOrganisation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) =>
      organisationService.updateOrganisation(id, data),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: organisationKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: organisationKeys.detail(variables.id),
      });
    },
  });
};

export const useDeleteOrganisation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: organisationService.deleteOrganisation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organisationKeys.lists() });
    },
  });
};

export const useDeleteOrganisationMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ organisationId, userId }) =>
      organisationService.deleteOrganisationMember(organisationId, userId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: organisationKeys.members(variables.organisationId),
      });
    },
  });
};

export const useUpdateOrganisationMembers = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ organisationId, members }) =>
      organisationService.updateOrganisationMembers(organisationId, members),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: organisationKeys.members(variables.organisationId),
      });
    },
  });
};

export const useChangeOrganisationOwner = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ organisationId, ownerId }) =>
      organisationService.changeOrganisationOwner(organisationId, ownerId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: organisationKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: organisationKeys.detail(variables.organisationId),
      });
      queryClient.invalidateQueries({
        queryKey: organisationKeys.members(variables.organisationId),
      });
    },
  });
};

export const useChangeOrganisationName = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ organisationId, name }) =>
      organisationService.changeOrganisationName(organisationId, name),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: organisationKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: organisationKeys.detail(variables.organisationId),
      });
    },
  });
};

export const useOrganisationRoles = (id, options = {}) => {
  return useQuery({
    queryKey: organisationKeys.roles(id),
    queryFn: () => organisationService.getOrganisationRoles(id),
    enabled: !!id,
    ...options,
  });
};

export const useUpdateOrganisationMemberRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ organisationId, userId, roleData }) =>
      organisationService.updateOrganisationMemberRole(
        organisationId,
        userId,
        roleData
      ),
    onSuccess: (data, variables) => {
      const orgId = parseInt(variables.organisationId);

      queryClient.invalidateQueries({
        queryKey: organisationKeys.roles(orgId),
      });
      queryClient.invalidateQueries({
        queryKey: organisationKeys.members(orgId),
      });
      // Also invalidate the general organisation lists in case role changes affect the display
      queryClient.invalidateQueries({
        queryKey: organisationKeys.lists(),
      });

      // Additional broad invalidation to ensure we catch all related queries
      queryClient.invalidateQueries({
        queryKey: ["organisations", "roles"],
        exact: false,
      });
    },
  });
};
